import {
  Button,
  Divider,
  Group,
  Loader,
  Stack,
  Tabs,
  Text,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { Download } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import PageHeader from '../../components/shared/PageHeader';
import {
  SpectraSection,
  StatisticsSection,
  WorkspaceExportModal,
  WorkspaceEditInfoModal,
  WorkspaceOverviewSection,
  WorkspaceSessionsSection,
  WorkspaceTabLabel,
  TrendChartSection,
  useSessionFullTractMetrics,
  useSpectralWorkspace,
  useWorkspaceExport,
  useWorkspaceGroupSummary,
  useWarmSpectraPsd,
} from '../../features/research/packages/spectral-workspace';
import { isWorkspaceChartsDataPending } from '../../features/research/packages/spectral-workspace/utils/sessionLoadingState';

export function SpectralAnalysisDetailPage() {
  const { analysisId } = useParams<{ analysisId: string }>();
  const workspace = useSpectralWorkspace(analysisId);

  if (!analysisId) {
    return <Navigate to='/research/spectral-normalization' replace />;
  }

  if (workspace.isLoadingAnalysis) {
    return (
      <Stack align='center' py='xl'>
        <Loader />
        <Text size='sm' c='dimmed'>
          Caricamento analisi…
        </Text>
      </Stack>
    );
  }

  if (workspace.loadError || !workspace.meta) {
    return (
      <Stack gap='sm' py='md'>
        <Text c='red'>
          {workspace.loadError ?? 'Impossibile caricare l’analisi.'}
        </Text>
        <Button
          variant='light'
          component='a'
          href='/research/spectral-normalization'
        >
          Torna all’hub
        </Button>
      </Stack>
    );
  }

  return <SpectralAnalysisDetailPageContent workspace={workspace} />;
}

type SpectralAnalysisDetailPageContentProps = {
  workspace: ReturnType<typeof useSpectralWorkspace>;
};

function SpectralAnalysisDetailPageContent({
  workspace,
}: SpectralAnalysisDetailPageContentProps) {
  const { meta } = workspace;

  if (!meta) {
    return null;
  }
  const [activeTab, setActiveTab] = useState('overview');
  const [visitedTabs, setVisitedTabs] = useState(
    () => new Set<string>(['overview'])
  );
  const [editInfoOpened, { open: openEditInfo, close: closeEditInfo }] =
    useDisclosure(false);
  const canExport = workspace.sessionList.length > 0;
  const isLoadingSessions = workspace.isLoadingSessions;
  const isParsingSessionEvents = workspace.isParsingSessionEvents;
  const isOverviewTabLoading = isLoadingSessions || isParsingSessionEvents;
  const isChartsTabLoading =
    isLoadingSessions ||
    isWorkspaceChartsDataPending(
      workspace.sessionList,
      workspace.readySessions
    );

  useEffect(() => {
    let cancelled = false;

    const startParse = () => {
      if (!cancelled) {
        workspace.ensureSessionEventsParsed();
      }
    };

    if ('requestIdleCallback' in window) {
      const idleId = window.requestIdleCallback(startParse, { timeout: 1_500 });
      return () => {
        cancelled = true;
        window.cancelIdleCallback(idleId);
      };
    }

    const timeoutId = globalThis.setTimeout(startParse, 100);
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [workspace.ensureSessionEventsParsed]);

  const sessionsForMetrics = useMemo(
    () =>
      activeTab === 'overview' || visitedTabs.has('sessions')
        ? workspace.readySessions
        : [],
    [activeTab, visitedTabs, workspace.readySessions]
  );

  const chartsPendingData = isChartsTabLoading;

  const { metrics: sessionMetrics, isWarming: isMetricsWarming } =
    useSessionFullTractMetrics(sessionsForMetrics);
  const groupSummary = useWorkspaceGroupSummary(
    workspace.sessionList,
    sessionMetrics
  );

  useWarmSpectraPsd({
    readySessions:
      visitedTabs.has('signals') || visitedTabs.has('spectra')
        ? workspace.readySessions
        : [],
    activeViewId: workspace.activeViewId,
    customSegments: workspace.customSegments,
    trendStart: workspace.trendStart,
    trendEnd: workspace.trendEnd,
    selectedSessionId: workspace.selectedSpectraSessionId,
  });

  const exportState = useWorkspaceExport({
    meta,
    sessionList: workspace.sessionList,
    readySessions: workspace.readySessions,
    isLoadingSessions,
    customSegments: workspace.customSegments,
    trendStart: workspace.trendStart,
    trendEnd: workspace.trendEnd,
    showOverlaySessions: workspace.showOverlaySessions,
    selectedOverlaySessionIds: workspace.selectedOverlaySessionIds,
    selectedSpectraSessionId: workspace.selectedSpectraSessionId,
    spectraStart: workspace.spectraStart,
    spectraEnd: workspace.spectraEnd,
    spectraLogScale: workspace.spectraLogScale,
  });

  const handleTabChange = useCallback(
    (value: string | null) => {
      if (!value || value === activeTab) return;

      setVisitedTabs(previous => new Set(previous).add(value));
      setActiveTab(value);
    },
    [activeTab]
  );

  return (
    <>
      <PageHeader
        title={meta.name}
        subtitle={
          meta.description ||
          'Workspace per confronto temporale di sessioni completed su dati HR effettivi.'
        }
        breadcrumbs={[
          { label: 'Ricerca', href: '/research' },
          {
            label: 'Normalizzazione spettrale',
            href: '/research/spectral-normalization',
          },
          { label: meta.name },
        ]}
        backUrl='/research/spectral-normalization'
        actions={
          <Group gap='sm'>
            <Button variant='subtle' onClick={openEditInfo}>
              Modifica informazioni
            </Button>
            <Button
              variant='light'
              leftSection={<Download size={16} />}
              onClick={exportState.open}
              disabled={!canExport || isOverviewTabLoading || isMetricsWarming}
              title={
                !canExport
                  ? 'Seleziona un gruppo con almeno una sessione'
                  : isOverviewTabLoading || isMetricsWarming
                    ? 'Attendi il caricamento delle sessioni'
                    : undefined
              }
            >
              Esporta Excel
            </Button>
          </Group>
        }
      />

      {workspace.isSaving && (
        <Text size='xs' c='dimmed' mt={-8} mb='xs'>
          Salvataggio in corso…
        </Text>
      )}

      <Divider my='md' />

      <Tabs value={activeTab} onChange={handleTabChange} keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab value='overview' disabled={isLoadingSessions}>
            <WorkspaceTabLabel
              label='Overview'
              loading={isOverviewTabLoading}
            />
          </Tabs.Tab>
          <Tabs.Tab value='sessions' disabled={isLoadingSessions}>
            <WorkspaceTabLabel
              label='Sessioni'
              loading={isOverviewTabLoading}
            />
          </Tabs.Tab>
          <Tabs.Tab value='signals' disabled={isChartsTabLoading}>
            <WorkspaceTabLabel
              label='Evoluzione segnali'
              loading={isChartsTabLoading}
            />
          </Tabs.Tab>
          <Tabs.Tab value='spectra' disabled={isChartsTabLoading}>
            <WorkspaceTabLabel label='Spettri' loading={isChartsTabLoading} />
          </Tabs.Tab>
          <Tabs.Tab value='statistics' disabled={isChartsTabLoading}>
            <WorkspaceTabLabel
              label='Validazione statistica'
              loading={isChartsTabLoading}
            />
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value='overview' pt='md'>
          <WorkspaceOverviewSection
            sessions={workspace.sessionList}
            readySessions={workspace.readySessions}
            summary={groupSummary}
            isLoadingSessions={isLoadingSessions}
            isMetricsWarming={isMetricsWarming}
          />
        </Tabs.Panel>

        <Tabs.Panel
          value='sessions'
          pt='md'
          keepMounted={visitedTabs.has('sessions')}
        >
          {visitedTabs.has('sessions') ? (
            <WorkspaceSessionsSection
              sessions={workspace.sessionList}
              activeSessionGroupId={workspace.activeSessionGroupId}
              analysisId={meta.analysisId}
              isLoadingSessions={isLoadingSessions}
              sessionMetrics={sessionMetrics}
              onActiveSessionGroupChange={
                workspace.handleActiveSessionGroupChange
              }
            />
          ) : null}
        </Tabs.Panel>

        <Tabs.Panel
          value='signals'
          pt='md'
          keepMounted={visitedTabs.has('signals')}
        >
          {visitedTabs.has('signals') ? (
            <TrendChartSection
              readySessions={workspace.readySessions}
              isLoading={chartsPendingData}
              selectedFeature={workspace.selectedFeature}
              onFeatureChange={workspace.setSelectedFeature}
              trendNormalizationEnabled={workspace.trendNormalizationEnabled}
              onTrendNormalizationChange={
                workspace.setTrendNormalizationEnabled
              }
              trendLowPassEnabled={workspace.trendLowPassEnabled}
              onTrendLowPassChange={workspace.setTrendLowPassEnabled}
              showOverlaySessions={workspace.showOverlaySessions}
              onShowOverlayChange={workspace.setShowOverlaySessions}
              selectedOverlaySessionIds={workspace.selectedOverlaySessionIds}
              onOverlaySessionChange={workspace.setSelectedOverlaySessionIds}
              activeViewId={workspace.activeViewId}
              onActiveViewChange={workspace.setActiveViewId}
              customSegments={workspace.customSegments}
              onCustomSegmentsChange={workspace.setCustomSegments}
              trendStart={workspace.trendStart}
              trendEnd={workspace.trendEnd}
              onTrendStartChange={workspace.setTrendStart}
              onTrendEndChange={workspace.setTrendEnd}
            />
          ) : null}
        </Tabs.Panel>

        <Tabs.Panel
          value='spectra'
          pt='md'
          keepMounted={visitedTabs.has('spectra')}
        >
          {visitedTabs.has('spectra') ? (
            <SpectraSection
              readySessions={workspace.readySessions}
              activeViewId={workspace.activeViewId}
              onActiveViewChange={workspace.setActiveViewId}
              customSegments={workspace.customSegments}
              onCustomSegmentsChange={workspace.setCustomSegments}
              trendStart={workspace.trendStart}
              trendEnd={workspace.trendEnd}
              onTrendStartChange={workspace.setTrendStart}
              onTrendEndChange={workspace.setTrendEnd}
              selectedSessionId={workspace.selectedSpectraSessionId}
              onSelectedSessionChange={workspace.setSelectedSpectraSessionId}
              logScale={workspace.spectraLogScale}
              onLogScaleChange={workspace.setSpectraLogScale}
              spectraStart={workspace.spectraStart}
              spectraEnd={workspace.spectraEnd}
              onSpectraStartChange={workspace.setSpectraStart}
              onSpectraEndChange={workspace.setSpectraEnd}
              computeEnabled={activeTab === 'spectra'}
            />
          ) : null}
        </Tabs.Panel>

        <Tabs.Panel
          value='statistics'
          pt='md'
          keepMounted={visitedTabs.has('statistics')}
        >
          {visitedTabs.has('statistics') ? (
            <StatisticsSection
              readySessions={workspace.readySessions}
              activeViewId={workspace.activeViewId}
              onActiveViewChange={workspace.setActiveViewId}
              customSegments={workspace.customSegments}
              onCustomSegmentsChange={workspace.setCustomSegments}
              trendStart={workspace.trendStart}
              trendEnd={workspace.trendEnd}
              onTrendStartChange={workspace.setTrendStart}
              onTrendEndChange={workspace.setTrendEnd}
            />
          ) : null}
        </Tabs.Panel>
      </Tabs>

      <WorkspaceExportModal
        opened={exportState.opened}
        onClose={exportState.close}
        exportingId={exportState.exportingId}
        isPreparingExportData={exportState.isPreparingExportData}
        onExport={exportState.handleExportSingle}
        canExport={canExport}
        excelExports={exportState.excelExports}
      />

      <WorkspaceEditInfoModal
        opened={editInfoOpened}
        onClose={closeEditInfo}
        initialName={meta.name}
        initialDescription={meta.description}
        onSave={workspace.updateMeta}
      />
    </>
  );
}

export function LegacyAnalysisPathRedirect() {
  const { analysisId } = useParams<{ analysisId: string }>();

  if (!analysisId) {
    return <Navigate to='/research/spectral-normalization' replace />;
  }

  return (
    <Navigate to={`/research/spectral-normalization/${analysisId}`} replace />
  );
}

export default SpectralAnalysisDetailPage;
